import { z } from 'zod';
import { AutoSuggestionSection } from '../shared/types';
import useLocationAutoSuggestManager from './LocationAutoSuggest.hooks';

export const locationSuggestionSchema = z.object({
  type: z.enum(['geolocation', 'locality', 'street', 'history']),
  label: z.string(),
  id: z.number().optional().nullable(),
  position: z
    .object({
      lat: z.number(),
      lon: z.number(),
    })
    .optional(),
});

export type LocationSuggestion = z.infer<typeof locationSuggestionSchema>;

export const anonymousLocationCookieSchema = z.object({
  label: z.string().min(1),
  id: z.number().optional(),
});

export type AnonymousLocationCookie = z.infer<typeof anonymousLocationCookieSchema>;

export type LocationSuggestionOption = {
  isHighlighted: boolean;
  query: string;
};

export type LocationAutoSuggestState = {
  locationValue: string;
  lat?: string;
  lon?: string;
  hasHistory: boolean;
  suggestions: AutoSuggestionSection<LocationSuggestion>[];
  storedSuggestions: AutoSuggestionSection<LocationSuggestion>[];
  showSuggestions: boolean;
  isFetchingUserPosition: boolean;
};

export type LocationAutoSuggestAction =
  | {
      type: 'ON_INPUT_CHANGE' | 'INIT_LOCATION_VALUE';
      payload: { value: string; lat?: string; lon?: string };
    }
  | {
      type: 'ON_SUGGESTION_LOADED';
      payload: {
        suggestions: AutoSuggestionSection<LocationSuggestion>[];
        storedSuggestions: AutoSuggestionSection<LocationSuggestion>[];
        hasHistory: boolean;
      };
    }
  | {
      type: 'SET_SHOW_SUGGESTIONS';
      payload: boolean;
    }
  | {
      type: 'FETCHING_USER_POSITION';
      payload: boolean;
    };

export type LocationAutoSuggestManagerProps = {
  initialState: LocationAutoSuggestState;
  loadFromHistory?: boolean;
  onSelectLocationSuggestion: ({ location, lat, lon }: { location: string; lat?: string; lon?: string }) => void;
  onNoLocationHistory?: () => void;
};

export type LocationAutoSuggestManagerOutput = ReturnType<typeof useLocationAutoSuggestManager>;
